"use client";

import React, { useEffect, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import {
  Home,
  Wallet,
  Plus,
  Send,
  FileText,
  HelpCircle,
  Menu,
  X,
  User,
  LogOut,
  Shield,
  ShieldCheck,
  ShieldHalf,
  GitPullRequest,
  ChartNetworkIcon,
  ArrowLeftFromLine,
} from "lucide-react";
import { usePrivy } from "@privy-io/react-auth";

export function Layout() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [smoothPosition, setSmoothPosition] = useState({ x: 0, y: 0 });
  const { authenticated, user: UserInfo, logout } = usePrivy();

  // Utility function to truncate wallet address
  const truncateAddress = (address: any) => {
    if (!address) return "Guest";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Linear interpolation for smoother trailing effect
  const lerp = (start: any, end: any, factor: any) => {
    return start + (end - start) * factor;
  };

  // Track mouse movement and update smooth position
  useEffect(() => {
    const handleMouseMove = (e: any) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);

    // Animation frame for smooth trailing
    let animationFrameId = null as any;
    const updateSmoothPosition = () => {
      setSmoothPosition((prev) => ({
        x: lerp(prev.x, cursorPosition.x, 0.1),
        y: lerp(prev.y, cursorPosition.y, 0.1),
      }));
      animationFrameId = requestAnimationFrame(updateSmoothPosition);
    };
    animationFrameId = requestAnimationFrame(updateSmoothPosition);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [cursorPosition]);

  const navigationItems = [
    {
      path: "https://taptapup.com/",
      icon: ArrowLeftFromLine,
      label: "Visit TapTapUp",
      external: true,
    },
    { path: "/dashboard", icon: Home, label: "Dashboard" },
    { path: "/wallet", icon: Wallet, label: "Wallet" },
    { path: "/add-funds", icon: Plus, label: "Add Funds" },
    { path: "/transfer", icon: Send, label: "Transfer" },
    { path: "/analytics", icon: ChartNetworkIcon, label: "Analytics" },
    {
      path: "/paymentRequests",
      icon: GitPullRequest,
      label: "Payment Requests",
    },
    { path: "/terms", icon: FileText, label: "Terms" },
    { path: "/faqs", icon: HelpCircle, label: "FAQs" },
  ];

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (isMobileMenuOpen && !event.target.closest(".mobile-menu-container")) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileMenuOpen]);

  return (
    <div className="bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] text-white min-h-screen">
      {/* Cursor Dot (Hidden on Mobile) */}
      <div
        className="fixed w-3 h-3 bg-[#61CE70] rounded-full pointer-events-none transition-transform duration-300 ease-out shadow-lg shadow-[#61CE70]/30 hidden lg:block"
        style={{
          transform: `translate(${smoothPosition.x - 6}px, ${
            smoothPosition.y - 6
          }px)`,
          zIndex: 9999,
        }}
      />

      {/* Header */}
      <header className="bg-[#0F1419]/80 backdrop-blur-xl z-[1000] border-b border-gray-800/50 px-4 sm:px-6 sticky top-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <img
              src="/logo.png"
              alt="Logo"
              className="w-20 h-10 sm:w-24 sm:h-24 object-contain"
            />
          </div>

          <div className="flex items-center space-x-3 sm:space-x-4">
            {/* Desktop User Menu */}
            <div className="hidden md:flex items-center space-x-2 sm:space-x-3">
              <button className="flex items-center space-x-2 p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <span className="text-xs sm:text-sm text-gray-300 hidden lg:block">
                  {authenticated
                    ? UserInfo?.email?.address ||
                      truncateAddress(UserInfo?.wallet?.address)
                    : "Guest"}
                </span>
              </button>
              {authenticated && (
                <button
                  onClick={() => logout()}
                  className="group flex items-center space-x-2 p-2 rounded-lg bg-gradient-to-r from-red-400 to-red-600 text-white font-medium transition-all duration-300 hover:shadow-lg hover:shadow-red-400/30 hover:scale-105"
                >
                  <LogOut className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  <span className="text-xs sm:text-sm text-white hidden lg:block">
                    Log Out
                  </span>
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-all duration-200 active:scale-95"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              ) : (
                <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar - Desktop */}
        <nav className="hidden md:flex w-64 lg:w-72 h-[calc(100vh-4rem)] bg-[#0F1419]/50 backdrop-blur-xl border-r border-gray-800/30 sticky top-16">
          <div className="flex flex-col w-full p-4 sm:p-6">
            <ul className="space-y-2 sm:space-y-3">
              {navigationItems.map(({ path, icon: Icon, label, external }) => (
                <li key={path}>
                  {external ? (
                    <a
                      href={path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative flex items-center space-x-3 sm:space-x-4 px-3 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm sm:text-base bg-gray-800/50 text-gray-300 hover:bg-gradient-to-r hover:from-[#61CE70]/10 hover:to-[#50B060]/10 hover:text-white border border-transparent hover:border-[#61CE70]/30"
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200 group-hover:scale-110 group-hover:text-[#61CE70]" />
                      <span className="font-medium">{label}</span>
                    </a>
                  ) : (
                    <Link
                      to={path}
                      className={`group flex items-center space-x-3 sm:space-x-4 px-3 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm sm:text-base ${
                        location.pathname === path
                          ? "bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white shadow-lg shadow-[#61CE70]/20"
                          : "text-gray-400 hover:bg-gray-800/50 hover:text-white"
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon
                        className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200 ${
                          location.pathname === path
                            ? ""
                            : "group-hover:scale-110"
                        }`}
                      />
                      <span className="font-medium">{label}</span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* User Info in Sidebar */}
            <div className="mt-auto pt-4 sm:pt-6 border-t border-gray-800/50">
              <div className="space-y-2 sm:space-y-3 p-3 sm:p-4 bg-gray-800/30 rounded-lg">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-[#61CE70] to-[#50B060] rounded-lg flex items-center justify-center">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-xs sm:text-sm">
                      {authenticated
                        ? UserInfo?.email?.address ||
                          truncateAddress(UserInfo?.wallet?.address)
                        : "Guest"}
                    </p>
                    <p className="text-gray-400 text-xs">Premium User</p>
                  </div>
                </div>
                {authenticated && (
                  <button
                    onClick={() => logout()}
                    className="group flex items-center justify-center space-x-2 w-full bg-gradient-to-r from-red-400 to-red-600 text-white font-medium py-2 px-3 sm:px-4 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-red-400/30 hover:scale-105"
                  >
                    <LogOut className="w-4 h-4 sm:w-4 sm:h-4 text-white group-hover:scale-110 transition-transform duration-300" />
                    <span className="text-xs sm:text-sm">Log Out</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden z-[1000] fixed inset-0 bg-black/60 backdrop-blur-sm mobile-menu-container">
            <div className="fixed top-0 left-0 w-4/5 sm:w-80 h-full bg-[#0F1419]/95 backdrop-blur-xl border-r border-gray-800/50 shadow-2xl">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-6 sm:mb-8">
                  <div className="flex items-center">
                    <img
                      src="/logo.png"
                      alt="Logo"
                      className="w-20 h-10 sm:w-24 sm:h-12 object-contain"
                    />
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </div>

                {/* User Info */}
                <div className="mb-6 sm:mb-8 p-3 sm:p-4 bg-gray-800/30 rounded-lg">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-[#61CE70] to-[#50B060] rounded-lg flex items-center justify-center">
                      <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm sm:text-base">
                        {authenticated
                          ? UserInfo?.email?.address ||
                            truncateAddress(UserInfo?.wallet?.address)
                          : "Guest"}
                      </p>
                      <p className="text-gray-400 text-xs sm:text-sm">
                        Premium User
                      </p>
                    </div>
                  </div>
                  {authenticated && (
                    <button
                      onClick={() => logout()}
                      className="group flex items-center justify-center space-x-2 w-full bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white font-medium py-2 px-3 sm:px-4 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-[#61CE70]/30 hover:scale-105 mt-3 sm:mt-4"
                    >
                      <LogOut className="w-4 h-4 text-white group-hover:scale-110 transition-transform duration-300" />
                      <span className="text-xs sm:text-sm">Log Out</span>
                    </button>
                  )}
                </div>

                <ul className="space-y-2 sm:space-y-3">
                  {navigationItems.map(
                    ({ path, icon: Icon, label, external }) => (
                      <li key={path}>
                        {external ? (
                          <a
                            href={path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center space-x-3 sm:space-x-4 px-3 sm:px-4 py-3 sm:py-4 rounded-lg transition-all duration-200 text-sm sm:text-base bg-gray-800/50 text-gray-300 hover:bg-gradient-to-r hover:from-[#61CE70]/10 hover:to-[#50B060]/10 hover:text-white border border-transparent hover:border-[#61CE70]/30"
                          >
                            <Icon className="w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-200 group-hover:scale-110 group-hover:text-[#61CE70]" />
                            <span className="font-medium">{label}</span>
                          </a>
                        ) : (
                          <Link
                            to={path}
                            className={`group flex items-center space-x-3 sm:space-x-4 px-3 sm:px-4 py-3 sm:py-4 rounded-lg transition-all duration-200 text-sm sm:text-base ${
                              location.pathname === path
                                ? "bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white shadow-lg shadow-[#61CE70]/20"
                                : "text-gray-400 hover:bg-gray-800/50 hover:text-white"
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <Icon
                              className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-200 ${
                                location.pathname === path
                                  ? ""
                                  : "group-hover:scale-110"
                              }`}
                            />
                            <span className="font-medium">{label}</span>
                          </Link>
                        )}
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 md:p-8 lg:p-10 max-w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-[#0F1419]/80 backdrop-blur-xl border-t border-gray-800/50 px-4 sm:px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-around space-y-6 md:space-y-0 md:space-x-4">
            {/* Compliance Info */}
            <div className="w-full md:w-1/2">
              <ul className="p-2 sm:p-4 space-y-4 sm:space-y-5">
                <li className="font-semibold text-sm sm:text-base">
                  <div className="flex items-center">
                    <Shield className="text-green-600 h-10 w-10 sm:h-12 sm:w-12 bg-green-400/20 rounded-lg p-2 mr-2 sm:mr-3" />
                    <div className="flex flex-col">
                      PayzTap is registered with FinCEN (USA) as a Licensed
                      Money Service Business
                      <span className="font-normal text-xs sm:text-sm text-gray-400">
                        Regulated and compliant financial services
                      </span>
                    </div>
                  </div>
                </li>
                <li className="font-semibold text-sm sm:text-base">
                  <div className="flex items-center">
                    <ShieldCheck className="text-green-600 h-10 w-10 sm:h-12 sm:w-12 bg-green-400/20 rounded-lg p-2 mr-2 sm:mr-3" />
                    <div className="flex flex-col">
                      PayzTap is registered with Dun & Bradstreet
                      <span className="font-normal text-xs sm:text-sm text-green-400">
                        D-U-N-S® Number: 13-968-1318
                      </span>
                    </div>
                  </div>
                </li>
                <li className="font-semibold text-sm sm:text-base">
                  <div className="flex items-center">
                    <ShieldHalf className="text-green-600 h-10 w-10 sm:h-12 sm:w-12 bg-green-400/20 rounded-lg p-2 mr-2 sm:mr-3" />
                    <div className="flex flex-col">
                      PayzTap is a registered entity under the Corporate
                      Transparency
                      <span className="font-normal text-xs sm:text-sm text-green-400">
                        Act (CTA). BOIR ID: 50000016298627
                      </span>
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            {/* Office Addresses */}
            <div className="flex flex-col items-center space-y-6 md:space-y-8 text-xs sm:text-sm text-gray-400 w-full md:w-1/2">
              <div className="text-center">
                <p className="text-green-400 tracking-wider font-bold mb-1 sm:mb-2">
                  San Francisco Office
                </p>
                <p>455 Market St Ste 1940</p>
                <p>San Francisco, CA 94105-2448</p>
              </div>
              <div className="w-full border border-gray-700"></div>
              <div className="text-center">
                <p className="text-green-400 tracking-wider font-bold mb-1 sm:mb-2">
                  Wyoming Office
                </p>
                <p>30 N Gould St Ste R</p>
                <p>Sheridan, WY 82801</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
